import React, { useState } from "react";
import { Coffee, Soup, Box, Printer, ArrowUpRight } from "lucide-react";

const categories = [
  {
    key: "paper-cups",
    icon: <Coffee size={18} />,
    title: "Paper Cups",
    blurb: "Premium food-grade paper cups in 6 sizes from 55ml to 210ml.",
    items: ["Tea Cups (55, 65, 75 ml)", "Coffee Cups (90, 150 ml)", "Ripple Paper Cups", "Double Wall Cups", "Printed Promotional Cups", "Ice Cream Cups (210 ml)"],
    img: "https://images.unsplash.com/photo-1574585046455-827418063fc1?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    key: "tissue",
    icon: <Soup size={18} />,
    title: "Tissue Products",
    blurb: "Soft, absorbent tissues for hospitality and home.",
    items: ["Tissue Napkins", "Dinner Napkins", "Facial Tissues", "Kitchen Towels", "Bulk Tissue Rolls"],
    img: "https://images.unsplash.com/photo-1583252751852-a2af5c6f9b8e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    key: "packaging",
    icon: <Box size={18} />,
    title: "Food Packaging",
    blurb: "Sturdy paper containers for takeaway, delivery and catering.",
    items: ["Paper Containers", "Food Boxes", "Paper Bowls", "Takeaway Packaging", "Custom Printed Packaging"],
    img: "https://images.unsplash.com/photo-1696960741206-63a41bb798ad?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    key: "custom",
    icon: <Printer size={18} />,
    title: "Custom Branding",
    blurb: "Make every cup a billboard — up to 6-color custom printing.",
    items: ["Logo Printed Paper Cups", "Custom Tissue Napkins", "Event Branding Products", "Restaurant Packaging Solutions"],
    img: "https://images.unsplash.com/photo-1615485736894-a2d2e6d4cd9a?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

export const Products = ({ onQuote, onCustomize }) => {
  const [active, setActive] = useState("paper-cups");
  const current = categories.find((c) => c.key === active);

  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-vc text-xs uppercase tracking-[0.25em] vc-text-gradient font-bold">
              Our Products
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-extrabold text-neutral-900">
              Everything disposable. <br />
              <span className="vc-text-gradient">Engineered to brand.</span>
            </h2>
          </div>
          <p className="text-neutral-600 max-w-md">
            From iconic morning chai cups to custom-printed packaging, our catalogue spans every disposable a modern business needs.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-12 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c.key}
              data-testid={`product-tab-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 h-11 text-sm font-semibold border transition-all ${
                active === c.key
                  ? "vc-gradient text-white border-transparent shadow-lg shadow-orange-500/20"
                  : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
              }`}
            >
              {c.icon}
              {c.title}
            </button>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-neutral-200 min-h-[420px]">
            <img src={current.img} alt={current.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="font-mono-vc text-[11px] uppercase tracking-[0.2em] opacity-80">
                Featured Range
              </div>
              <div className="font-display text-3xl md:text-4xl font-extrabold mt-1">
                {current.title}
              </div>
              <p className="mt-2 text-white/85 max-w-lg">{current.blurb}</p>
            </div>
          </div>

          <div className="lg:col-span-5 rounded-3xl border border-neutral-200 p-7 bg-neutral-50/70">
            <div className="font-display text-2xl font-bold text-neutral-900">In this range</div>
            <ul className="mt-5 space-y-3">
              {current.items.map((it) => (
                <li key={it} className="flex items-center justify-between gap-3 py-3 border-b border-neutral-200 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full vc-gradient" />
                    <span className="text-neutral-800 font-medium">{it}</span>
                  </div>
                  <ArrowUpRight size={16} className="text-neutral-400" />
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={onQuote}
                data-testid={`products-quote-${current.key}`}
                className="vc-gradient text-white rounded-full h-11 px-5 text-sm font-semibold"
              >
                Get Bulk Pricing
              </button>
              {current.key === "paper-cups" && (
                <button
                  onClick={onCustomize}
                  data-testid="products-customize-cup"
                  className="rounded-full h-11 px-5 text-sm font-semibold border border-neutral-300 hover:bg-neutral-100"
                >
                  Customize a Cup ↓
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
